// Services
import { apiInstance, gql } from "@/services/httpAdapter";

// Helpers
import { errorLogger } from "@Common/helpers/logger";

// API
import { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";

// Types
import { IReviewData, IReviewDetailedResponse } from "@DataPages/types/socialMediaReviews/model";
import { Nullable } from "@Common/interfaces/types";
import { IReviewDateRange } from "@DataPages/types/socialMediaReviews/utils";

interface IGetReviewDetails {
	reputationMgmtUserId?: string;
	siteName?: string;
	dateRange?: IReviewDateRange | null;
	timezone: string;
	source: "store" | "users";
}

export async function getSocialMediaReview({
	timezone,
	locationId,
	commissionCycleId,
	payPeriodEndDate,
}: {
	locationId: string;
	commissionCycleId: string;
	payPeriodEndDate: string;
	timezone: string;
}): Promise<Nullable<IReviewData>> {
	const query = gql`
		query getSocialMediaReview($locationId: String, $commissionCycleId: String, $payPeriodEndDate: String, $timezone: String) {
			getSocialMediaReview(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodEndDate: $payPeriodEndDate
				timezone: $timezone
			) {
				_id
				commissionCycleId
				payPeriodId
				periodStartDate
				periodEndDate
				locationId
				onDemand
				usersReviews {
					positionTitle
					sitesReviews {
						name
						users {
							platformReviewCounts {
								averageRating
								reviewCount
								siteName
								neededResponseTotal
							}
							totalReviews
							user {
								_id
								fullName
								position
								employeeId
								reputationMgmtUserId
							}
						}
					}
				}
				storeReviews {
					averageRating
					reviewCount
					siteName
					neededResponseTotal
				}
				createdAt
				updatedAt
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodEndDate,
		timezone,
	};

	try {
		const response: GraphqlQueryResponse<"getSocialMediaReview", IReviewData> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSocialMediaReview;
	} catch (error) {
		errorLogger(error as Error, {
			function: "getSocialMediaReview",
			message: "Error while fetching social media review data",
			variables,
		});
		return null;
	}
}

export async function getReviewDetails(
	locationId: string,
	commissionCycleId: string,
	filter: IGetReviewDetails
): Promise<Nullable<IReviewDetailedResponse>> {
	const query = gql`
		query getReviewDetails($locationId: String, $commissionCycleId: String, $filter: ReviewDetailsFilterInput) {
			getReviewDetails(locationId: $locationId, commissionCycleId: $commissionCycleId, filter: $filter) {
				reviewDetails {
					reviewUid
					author
					commissionCycleId
					isRecommendation
					locationId
					needsResponse
					payPeriodId
					reviewDate
					responses {
						body
						createdAt
						siteAuthorName
					}
					attributions {
						reviewInvitationUid
						userUid
						apiClientUid
					}
					review {
						body
						rating
						siteName
					}
				}
				attributedUsers {
					position
					fullName
					reputationMgmtUserId
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		filter,
	};

	try {
		const response: GraphqlQueryResponse<"getReviewDetails", IReviewDetailedResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getReviewDetails;
	} catch (error) {
		errorLogger(error as Error, {
			function: "getReviewDetails",
			message: "Error while fetching review details data",
			variables,
		});
		return null;
	}
}

export async function getSocialMediaReviewDetails(
	locationId: string,
	dateRange: IReviewDateRange,
	timezone: string
): Promise<Nullable<IReviewData>> {
	const query = gql`
		query getSocialMediaReviewDetails($locationId: String!, $dateRange: DateReviewsRangesInput, $timezone: String!) {
			getSocialMediaReviewDetails(locationId: $locationId, dateRange: $dateRange, timezone: $timezone) {
				_id
				locationId
				onDemand
				storeReviews {
					averageRating
					reviewCount
					siteName
					neededResponseTotal
				}
				createdAt
				updatedAt
				usersReviews {
					positionTitle
					sitesReviews {
						name
						users {
							platformReviewCounts {
								averageRating
								reviewCount
								siteName
								neededResponseTotal
							}
							totalReviews
							user {
								_id
								employeeId
								fullName
								position
								reputationMgmtUserId
							}
						}
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
		dateRange,
		timezone,
	};

	try {
		const response: GraphqlQueryResponse<"getSocialMediaReviewDetails", IReviewData> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSocialMediaReviewDetails;
	} catch (error) {
		errorLogger(error as Error, {
			function: "getSocialMediaReviewDetails",
			message: "Error while fetching social media review details data.",
			variables,
		});
		return null;
	}
}
